import ObjectId from 'bson-objectid';
import {
    Avatar, Button, Clickable, Col,
    Divider, Dropdown, Icon, Input, Row, Sidepanel, Spinner2, Text, TextArea
} from 'components';
import { Api, capitalize, sports, theme, updateTypes } from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    CreateUserPanel, DatePicker,
    IrreversableModal, SearchUsers
} from 'screens/Admin/components';
import { ConcludeModal, Settings } from './components';

let timer;
const EventPanel = ({ visible, closeModal, dataId }) => {
  const [sport, setSport] = useState(null),
    [user, setUser] = useState(null),
    [type, setType] = useState(''),
    [text, setText] = useState(null),
    [location, setLocation] = useState(''),
    [date, setDate] = useState(null),
    [userType, setUserType] = useState('athlete'),
    [users, setUsers] = useState([null, null]),
    [errs, setErrs] = useState({}),
    [search, setSearch] = useState(''),
    [searchActive, setSearchActive] = useState(false),
    [visibleModal, setVisibleModal] = useState(false),
    isMobile = useSelector((state) => state.appState.isMobile),
    inputRef = useRef(),
    textRef = useRef(),
    navigate = useNavigate(),
    dispatch = useDispatch(),
    foundSport = sports.find((x) => x.value === sport) || {},
    storedData = useSelector((state) => state.admin.updates.find((x) => x._id === dataId)),
    handledUserType = type === 'game' ? (foundSport?.teamSport ? 'team' : 'athlete') : userType,
    isLive = new Date() > new Date(date) && !storedData?.score?.length;

  useEffect(() => {
    setSport(storedData?.sport ? capitalize(storedData.sport) : '');
    setType(storedData ? storedData.type : '');
    setText(storedData ? storedData.text : '');
    setLocation(storedData ? storedData.location : '');
    setDate(storedData ? new Date(storedData.date) : '');
    setUsers(storedData ? storedData.users : []);
  }, [visible]);

  useEffect(() => {
    setErrs({
      ...errs,
      sportEvent: !sport || !type,
      noUsers: users.filter((x) => x).length < (type === 'game' ? 2 : 1),
      date: !date,
      text: !text
    });
  }, [sport, type, users, date, text]);

  async function remove() {
    const res = await Api.post(
      `/admin/removeData`,
      { type: 'EventUpdate', id: dataId },
      'removeUpdate'
    );
    if (!res) return;

    setVisibleModal(false);
    closeModal();
  }

  async function submit() {
    const sportEvent = !sport || !type,
      noUsers = users.filter((x) => x).length < (type === 'game' ? 2 : 1);

    if (sportEvent || noUsers || !date || !text) {
      setErrs({
        sportEvent,
        noUsers,
        date: !date,
        text: !text
      });

      return;
    }

    if (type === 'game') {
      const notSameType = users.find((x) => {
        return foundSport.teamSport ? x.type !== 'team' : x.type !== 'athlete';
      });

      const notSameSport = users.find((x) => {
        return (x.sport || x.sports[0].sport).toLowerCase() !== sport.toLowerCase();
      });

      if (notSameSport || notSameType) {
        dispatch({
          type: 'HANDLE_RES',
          payload: {
            err: 'Selected athlete and teams must share the same user type(Team or Athlete), and same sport.'
          }
        });

        return;
      }
    }

    if (!storedData && new Date(date) < new Date()) {
      dispatch({
        type: 'HANDLE_RES',
        payload: { err: 'Date cannot be earlier than the current date!' }
      });

      return;
    }

    const documentId = storedData?._id || ObjectId().toString();
    const data = {
      _id: documentId,
      sport: sport.toLowerCase(),
      users: users.map((x) => x?._id),
      userType: (userType
        ? userType
        : type === 'game' && foundSport.teamSport
          ? 'team'
          : 'athlete'
      ).toLowerCase(),
      date: new Date(date),
      type,
      text,
      location
    };

    const res = await Api.post('/admin/addEventUpdate', data, 'addEventUpdate');
    if (!res) return;

    closeModal();
  }

  if (isMobile) {
    return (
      <Col center pad="20px" ht="100vh">
        <Text h1>Please visit this screen on a computer browser.</Text>
      </Col>
    );
  }

  return (
    <React.Fragment>
      <IrreversableModal
        visible={visibleModal === 'remove'}
        heading="You are about to remove this update."
        onConfirm={remove}
        closeModal={() => setVisibleModal(null)}
      />

      <ConcludeModal
        visible={visibleModal === 'conclude'}
        closeModal={() => {
          setVisibleModal(null);
          closeModal();
        }}
        data={storedData}
      />

      <Sidepanel wid={600} pad="0" visible={visible} closeModal={closeModal}>
        <Col pos="sticky" bg="#fff" zIndex="1" center top="0">
          <Row between center pad="12px 24px 0">
            <Text h3>{storedData ? 'Edit event' : 'Create new event'}</Text>

            <Settings
              closeModal={closeModal}
              update={storedData}
              isLive={isLive}
              visible={visibleModal}
              setVisible={setVisibleModal}
            />
          </Row>

          <Divider marg="12px 0 0" />
        </Col>

        {dataId && !storedData && (
          <Col
            zIndex={5}
            pos="absolute"
            ht="100%"
            wid="100%"
            bg="rgba(210, 216, 223, 0.7)"
            left="0"
            centerAll
          >
            <Spinner2 col={theme.SECONDARY} />
          </Col>
        )}

        <Col center pad="24px">
          <Col ht="100%" start>
            <Col noFlex>
              <Text marg="0 0 24px" h4>
                Sport & event type
              </Text>
              {errs.sportEvent && (
                <Text col={theme.SECONDARY} c1 med marg="0 0 10px">
                  Required
                </Text>
              )}

              <Dropdown
                admin
                handleSelect={(e) => setSport(e.name)}
                translatePrefix="sports"
                options={sports}
                selected={sport}
                title="Sport"
                placeholder="Select a sport"
                wid="100%"
                rounded
                left
                bg="#fff"
              />

              <Dropdown
                admin
                marg="24px 0 0"
                handleSelect={(x) => setType(updateTypes.find((y) => x.value === y.value).opt)}
                options={updateTypes}
                selected={updateTypes.find((x) => x.opt === type)}
                placeholder="Select an event type"
                title="Type"
                wid="100%"
                rounded
                bg="#fff"
              />

              {sport && type && type !== 'game' && (
                <Dropdown
                  admin
                  marg="24px 0 0"
                  handleSelect={setUserType}
                  options={['Athlete', 'Team']}
                  selected={capitalize(userType)}
                  title="User type"
                  rounded
                  wid="100%"
                  bg="#fff"
                />
              )}

              {type === 'game' && sport && type ? (
                <Text h4 marg="40px 0 24px">
                  Between
                </Text>
              ) : (
                sport &&
                type && (
                  <Text h4 marg="40px 0 24px">
                    {capitalize(handledUserType || '')}
                  </Text>
                )
              )}
              {errs.noUsers && sport && type && (
                <Text marg="0 0 10px" col={theme.SECONDARY} c1 med>
                  Required
                </Text>
              )}

              {sport && type && (
                <React.Fragment>
                  <Row center>
                    <SearchUsers
                      sport={sport}
                      type={type}
                      handleSelect={(val) => setUsers([val, users[1]])}
                      userType={handledUserType}
                      selected={users[0]}
                      exclude={users.map((x) => x?._id).filter((x) => x)}
                    />

                    {!users[0] && (
                      <CreateUserPanel
                        marg="0 0 20px 20px"
                        onCreate={(val) => setUsers([val, users[1]])}
                      />
                    )}
                  </Row>

                  {users[0] && (
                    <Row noFlex start marg="-10px 0 20px">
                      <Row center between wid="100%" pad="0 16px">
                        <Row noFlex center>
                          <Avatar small user={users[0]} />
                          <Text marg="0 0 0 8px">{users[0].name}</Text>
                        </Row>

                        <Clickable onClick={() => setUsers([null, users[1]])}>
                          <Icon type="close" dimensions={14} />
                        </Clickable>
                      </Row>
                    </Row>
                  )}
                </React.Fragment>
              )}

              {type === 'game' && sport && type && (
                <Col wid="100%" noFlex>
                  <Text med size="16px" marg="0 0 20px 20px">
                    VS.
                  </Text>
                  <Row center>
                    <SearchUsers
                      sport={sport}
                      type={type}
                      userType={handledUserType}
                      handleSelect={(val) => setUsers([users[0], val])}
                      selected={users[1]}
                      exclude={users.map((x) => x?._id).filter((x) => x)}
                    />

                    {!users[1] && (
                      <CreateUserPanel
                        marg="0 0 20px 20px"
                        onCreate={(val) => setUsers([users[0], val])}
                      />
                    )}
                  </Row>

                  {users[1] && (
                    <Row noFlex start marg="-10px 0 20px">
                      <Row center between wid="100%" pad="0 16px">
                        <Row noFlex center>
                          <Avatar small user={users[1]} />
                          <Text marg="0 0 0 8px">{users[1].name}</Text>
                        </Row>

                        <Clickable onClick={() => setUsers([users[0], null])}>
                          <Icon type="close" dimensions={14} />
                        </Clickable>
                      </Row>
                    </Row>
                  )}
                </Col>
              )}

              <Text h4 marg="40px 0 24px">
                Location & date
              </Text>

              <Input
                onChange={({ target }) => setLocation(target.value)}
                value={location}
                disabled={type === 'game' ? users.length < 2 : !users.length || !type || !sport}
                marg="0 0 24px"
                title="Location"
                placeholder="eg. Anfield, Liverpool"
                bg="#fff"
              />

              <Text size="14px" med marg="0 16px 8px">
                Date
              </Text>
              {errs.date && (
                <Text marg="0 0 10px" col={theme.SECONDARY} c1 med>
                  Required
                </Text>
              )}

              <DatePicker selected={date} handleSelect={setDate} />

              <Col noFlex>
                <Text marg="40px 0 24px" h4>
                  Description
                </Text>

                {errs.text && (
                  <Text marg="0 0 10px" col={theme.SECONDARY} c1 med>
                    Required
                  </Text>
                )}

                <Text c1 marg="0 0 10px">
                  Eg. "Celebrate Neymar's 100th goal!"
                </Text>

                <TextArea
                  admin
                  onChange={({ target }) => setText(target.value)}
                  multiline
                  ref={textRef}
                  value={text}
                  style={{
                    width: 520,
                    maxWidth: 520,
                    minWidth: 520,
                    height: 80,
                    maxHeight: 80,
                    minHeight: 80,
                    fontFamily: 'CeraPro',
                    fontSize: 16,
                    border: '1px solid #ddd',
                    borderRadius: 15,
                    outline: 'none',
                    resize: 'none'
                  }}
                />
              </Col>

              <Row marg="56px 0 0">
                <Button big text="Cancel" onClick={closeModal} marg="0 24px 0 0" bordered />

                <Button
                  big
                  onClick={submit}
                  text={storedData ? 'Edit event' : 'Create events'}
                  bg={theme.SECONDARY}
                  action="addEventUpdate"
                  disabled={Object.keys(errs).find((key) => errs[key])}
                />
              </Row>
            </Col>
          </Col>
        </Col>
      </Sidepanel>
    </React.Fragment>
  );
};

export default EventPanel;
